import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Star, Medal, Crown, TrendingUp, TrendingDown, Target, Users, Filter } from 'lucide-react';
import { useLeaderboard } from '@/hooks/useGameManagement';
import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LeaderboardDisplayProps {
  defaultEntityType?: 'player' | 'coach' | 'team';
  showFilters?: boolean;
  limit?: number;
}

export const LeaderboardDisplay = ({ 
  defaultEntityType = 'player', 
  showFilters = true,
  limit 
}: LeaderboardDisplayProps) => {
  const [entityType, setEntityType] = useState<'player' | 'coach' | 'team'>(defaultEntityType);
  const [seasonYear, setSeasonYear] = useState<number>(new Date().getFullYear());
  
  const { leaderboard, loading, loadLeaderboard } = useLeaderboard();

  useEffect(() => {
    loadLeaderboard(entityType, undefined, seasonYear);
  }, [entityType, seasonYear, loadLeaderboard]);

  const getRankIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Crown className="h-6 w-6 text-yellow-500 drop-shadow-lg" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400 drop-shadow-lg" />;
      case 3:
        return <Trophy className="h-6 w-6 text-orange-600 drop-shadow-lg" />;
      default:
        return (
          <div className="w-6 h-6 flex items-center justify-center text-sm font-bold text-muted-foreground bg-muted rounded-full">
            {position}
          </div>
        );
    }
  };

  const formatWinRate = (winRate: number) => {
    return `${(winRate * 100).toFixed(1)}%`;
  };

  const getDisplayedEntries = () => {
    return limit ? leaderboard.slice(0, limit) : leaderboard;
  };

  const getSeasonOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <div className="text-center space-y-4">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
            <div className="text-muted-foreground">Loading leaderboard...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
          <h3 className="font-semibold text-lg mb-2">No Rankings Yet</h3>
          <p className="text-muted-foreground mb-4">
            Complete some games to see rankings appear here
          </p>
          <div className="text-sm text-muted-foreground bg-muted p-4 rounded-lg max-w-md mx-auto">
            Rankings are updated automatically after each completed game. 
            Win games to climb the leaderboard!
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {showFilters && (
        <Card className="lg:hidden">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Category</label>
                <Select value={entityType} onValueChange={(value) => setEntityType(value as 'player' | 'coach' | 'team')}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="player">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Players
                      </div>
                    </SelectItem>
                    <SelectItem value="coach">
                      <div className="flex items-center gap-2">
                        <Trophy className="h-4 w-4" />
                        Coaches
                      </div>
                    </SelectItem>
                    <SelectItem value="team">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4" />
                        Teams
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Season</label>
                <Select value={seasonYear.toString()} onValueChange={(value) => setSeasonYear(parseInt(value))}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getSeasonOptions().map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {showFilters && (
        <div className="hidden lg:flex items-center gap-4 p-4 bg-card border rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Target className="h-4 w-4" />
            Filter Rankings:
          </div>
          
          <Select value={entityType} onValueChange={(value) => setEntityType(value as 'player' | 'coach' | 'team')}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="player">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Players
                </div>
              </SelectItem>
              <SelectItem value="coach">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  Coaches
                </div>
              </SelectItem>
              <SelectItem value="team">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Teams
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={seasonYear.toString()} onValueChange={(value) => setSeasonYear(parseInt(value))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {getSeasonOptions().map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year} Season
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-sports-green" />
            <span className="bg-gradient-to-r from-sports-green to-primary bg-clip-text text-transparent">
              {entityType.charAt(0).toUpperCase() + entityType.slice(1)} Leaderboard
            </span>
            <Badge variant="outline" className="ml-auto">
              {seasonYear} Season
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {getDisplayedEntries().map((entry, index) => {
              const position = index + 1;
              const isTopThree = position <= 3;
              const isTopRank = position === 1;
              
              return (
                <div
                  key={entry.id}
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-lg border transition-all duration-300 hover:shadow-lg hover:scale-[1.02]",
                    isTopRank && "bg-gradient-to-r from-yellow-500/10 via-yellow-400/5 to-transparent border-yellow-500/30 shadow-lg",
                    position === 2 && "bg-gradient-to-r from-gray-400/10 via-gray-300/5 to-transparent border-gray-400/30",
                    position === 3 && "bg-gradient-to-r from-orange-600/10 via-orange-500/5 to-transparent border-orange-600/30",
                    !isTopThree && "hover:bg-muted/30"
                  )}
                >
                  <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                    {getRankIcon(position)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={cn(
                        "font-semibold text-base truncate",
                        isTopRank && "text-yellow-600 dark:text-yellow-400"
                      )}>
                        {entityType.charAt(0).toUpperCase() + entityType.slice(1)} #{entry.entity_id.slice(0, 8)}
                      </p>
                      {isTopRank && (
                        <Badge className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black text-xs px-2 py-0.5">
                          Champion
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{entry.games_played} games</span>
                      <span>•</span>
                      <span>{entry.total_points} pts</span>
                      {entry.last_game_date && (
                        <>
                          <span>•</span>
                          <span>Last game {new Date(entry.last_game_date).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center hidden sm:block">
                      <div className={cn(
                        "font-bold text-lg leading-none",
                        entry.games_won > 0 ? "text-sports-green" : "text-muted-foreground"
                      )}>
                        {entry.games_won}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">W</div>
                    </div>
                    
                    <div className="text-center hidden sm:block">
                      <div className={cn(
                        "font-bold text-lg leading-none",
                        entry.games_lost > 0 ? "text-destructive" : "text-muted-foreground"
                      )}>
                        {entry.games_lost}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">L</div>
                    </div>
                    
                    {entry.games_drawn > 0 && (
                      <div className="text-center hidden md:block">
                        <div className="font-bold text-lg leading-none text-muted-foreground">
                          {entry.games_drawn}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">D</div>
                      </div>
                    )}
                    
                    <div className="text-center">
                      <div className={cn(
                        "font-bold text-lg leading-none",
                        entry.win_rate >= 0.7 ? "text-sports-green" : 
                        entry.win_rate >= 0.5 ? "text-primary" : "text-destructive"
                      )}>
                        {formatWinRate(entry.win_rate)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Rate</div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    {entry.games_forfeited > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {entry.games_forfeited}F
                      </Badge>
                    )}
                    
                    {/* Mobile stats for small screens */}
                    <div className="sm:hidden text-xs text-muted-foreground">
                      {entry.games_won}W-{entry.games_lost}L
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {limit && leaderboard.length > limit && (
            <div className="text-center mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-3">
                Showing top {limit} of {leaderboard.length} entries
              </p>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                View Full Leaderboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};